import { ApolloClient, useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  AUTH_CHANGE_TRIGGER,
  getAuthTokenUnsafe,
  getDecodedTokenUnsafe,
  removeAuthToken,
  setAuthToken,
} from './authTokenUtils';
import { useGetUserQuery } from './requests/getUser.gql.generated';
import { User } from './types';
import { useIdentifyHeapUser } from '~/contexts/AnalyticsProvider';
import { useIdentifyLDUser } from '~/contexts/FeatureFlagProvider';
import { useIdentifySentryUser } from '~/contexts/MonitoringProvider';

// Types
type ContextValueCommon = {
  /**
   * User auth state transitions:
   * - 'logged-out': no auth token, user isn't logged in
   * - 'loading': have an auth token, still fetching user metadata
   * - 'logged-in': have token, done fetching user metadata
   * - 'error': failed to fetch user metadata (or other errors)
   */
  status: unknown;
  /**
   * User and organization data
   */
  user: unknown;
  /**
   * Logs users out by:
   * - removing the auth token cookie
   * - clearing Sentry user scope
   * - clearing Apollo cache
   * - redirecting to `/`
   */
  logout: (redirectPath?: string) => void;
  /**
   * Logs users in by:
   * - setting the auth token cookie
   * - redirecting to provided path (or reloading window if no path provided)
   */
  login: (authToken: string, redirectPath?: string | null) => void;
  /**
   * @deprecated Replace with URL query param-based storage of redirect paths. Stores the URL to
   * redirect to after login. This is read in the login form
   */
  redirectPath: string | null;
  /**
   * @deprecated Replace with URL query param-based storage of redirect paths. Sets the URL to
   * redirect to after login. This happens in routers, when we detect user is not logged in
   */
  setRedirectPath: (path: string | null) => void;
};
type ContextValueLoggedOut = { status: 'logged-out'; user: null; error: null };
type ContextValueLoggedIn = { status: 'logged-in'; user: User; error: null };
type ContextValueLoading = { status: 'loading'; user: null; error: null };
type ContextValueError = { status: 'error'; user: null; error: Error };
type ContextValue =
  | (ContextValueLoggedOut & ContextValueCommon)
  | (ContextValueLoggedIn & ContextValueCommon)
  | (ContextValueLoading & ContextValueCommon)
  | (ContextValueError & ContextValueCommon);

// Constants
const defaultContextValue: ContextValue = {
  status: 'loading',
  user: null,
  error: null,
  logout: () => {},
  login: () => {},
  redirectPath: null,
  setRedirectPath: () => {},
};

// Contexts
export const UserAuthContext = createContext<ContextValue>(defaultContextValue);

// Helpers
export const login: ContextValue['login'] = (authToken, redirectPath) => {
  setAuthToken(authToken);
  if (redirectPath) {
    window.location.replace(redirectPath);
  } else {
    window.location.reload();
  }
};

const createLogoutFn = (apolloClient: ApolloClient<object>) => (
  redirectPath: string = '/',
) => {
  // Remove auth cookie, clear Sentry user scope
  removeAuthToken();
  // reset Apollo cache and remove cookie to logout
  // https://stackoverflow.com/questions/48887480/reset-store-after-logout-with-apollo-client
  apolloClient.clearStore();
  apolloClient.cache.reset().finally(() => {
    window.location.replace(redirectPath);
  });
};

// Exports
type Props = { children: React.ReactNode; enableCrossTabLogout?: boolean };
export const UserAuthProvider = ({
  children,
  enableCrossTabLogout = false,
}: Props) => {
  const authToken = getAuthTokenUnsafe();
  const { data: userData, loading, error } = useGetUserQuery({
    // If we don't have a token, no use trying to get user data
    skip: !authToken,
  });
  const user = userData?.user;

  // Derive user auth status
  const status = useMemo(() => {
    // No token, we're logged out
    if (!authToken) return 'logged-out';
    // Can't decode token, or there's a user metadata fetch error
    if (!getDecodedTokenUnsafe() || error) return 'error';
    // Still fetching user metadata
    if (loading) return 'loading';
    // Sanity check to make sure we got back user metadata
    if (user) return 'logged-in';

    // If we end up here, something went very wrong
    Sentry.captureException(new Error('Failed to determine user auth status'));
    return 'error';
  }, [authToken, error, loading, user]);

  // Handle user metadata fetch errors
  useEffect(() => {
    if (error) Sentry.captureException(error);
  }, [error]);

  // Identify users with various third-party tools
  const { identifySentryUser } = useIdentifySentryUser();
  const { identifyLDUser } = useIdentifyLDUser();
  const { identifyHeapUser } = useIdentifyHeapUser();
  useEffect(() => {
    const userType = getDecodedTokenUnsafe()?.userType;
    if (user && userType) {
      identifySentryUser(user);
      identifyLDUser(user);
      identifyHeapUser(user, userType);
    }
  }, [identifyHeapUser, identifyLDUser, identifySentryUser, user]);

  // The logout function has a dependency on Apollo Client, but client instance can change based on
  // <ApolloClientProvider />, so we need to memoize the logout function here.
  const apolloClient = useApolloClient();
  const logout = useMemo(() => createLogoutFn(apolloClient), [apolloClient]);

  // Log in/out across all tabs. Tabs communicate with each other by listening to changes to a local
  // storage key. This key is set to latest timestamp whenever a user logs in or out, which causes
  // the `storage` even to be dispatched. `storage` events are only triggered in tabs _other_ than
  // the one where the user login/logout interaction was performed, according to web storage spec:
  // See https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (enableCrossTabLogout && event.key === AUTH_CHANGE_TRIGGER) {
        window.location.replace('/');
      }
    };
    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [enableCrossTabLogout, logout]);

  // Login redirect path state
  // !TODO @eldarshamukhamedov Replace with URL query params, no need for React state
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    if (status === 'logged-out' && redirectPath === null) {
      setRedirectPath(location.pathname + location.search);
    }
  }, [status, redirectPath, location, setRedirectPath]);

  // Assemble the context value
  const contextValue: ContextValue = useMemo(() => {
    const common = {
      login,
      logout,
      redirectPath,
      setRedirectPath,
    };
    if (status === 'error' && error) {
      return { status, user: null, error, ...common };
    }
    if (status === 'logged-out') {
      return { status, user: null, error: null, ...common };
    }
    if (status === 'logged-in' && user) {
      return { status, user, error: null, ...common };
    }
    if (status === 'loading') {
      return { status, user: null, error: null, ...common };
    }

    throw Error('Invalid user auth status');
  }, [error, logout, status, user, redirectPath, setRedirectPath]);

  // Logging state for debugging purposes
  useEffect(() => {
    if (!commonUtils.EnvUtils.isProduction()) {
      // eslint-disable-next-line no-console
      console.log('[UserAuthProvider] Context change', {
        status: contextValue.status,
        userId: contextValue.user?.id,
        userType: contextValue.user?.userType,
        orgSlug: contextValue.user?.organization.slug,
        redirectPath: contextValue.redirectPath,
      });
    }
  }, [contextValue]);

  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
    </UserAuthContext.Provider>
  );
};

/**
 * Directly access the UserAuthProvider context. User might not be logged in yet, or at all, on
 * un-autheticated routes. You should use `status` to handle loading and error states explicitly.
 *
 * If you are working in a part of the app where user is known to be logged in, you should use the
 * `useUserAuthOrThrow` hook instead.
 */
export const useUserAuthUnsafe = () => useContext(UserAuthContext);

/**
 * Returns logged-in user auth context. Throws an error if user is not logged-in.
 *
 * If you are working in a part of the app where the user is not logged in, or you need to handle
 * loading -> logged-in transitions, use the `useUserAuthUnsafe` hook instead.
 */
export const useUserAuthOrThrow = () => {
  const userAuth = useContext(UserAuthContext);
  if (userAuth.status !== 'logged-in') {
    throw Error('Expected user to be logged in in call to useUserAuthOrThrow');
  }
  return userAuth;
};
